import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const SUBMITTING_ATTRIBUTE = 'data-protocol-submitting';
const CART_REQUEST_TIMEOUT = 15000;
let protocolBuyBoxInstanceCounter = 0;

/**
 * Purchase option controller for the Sun Powder 90-day protocol module.
 *
 * @extends Component
 */
class SunpowderProtocolBuyBox extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Array<any>} */
  #sellingPlanGroups = [];

  /** @type {any} */
  #currentVariant = null;

  /** @type {string} */
  #defaultButtonText = '';

  /** @type {string} */
  #buyOnceBaseText = '';

  /** @type {string} */
  #instanceId = '';

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#instanceId = `sunpowder-protocol-${++protocolBuyBoxInstanceCounter}-${Math.random().toString(36).slice(2)}`;
    this.dataset.protocolJsVersion = 'v14-variant-safe-homepage';

    this.#sellingPlanGroups = this.#readJson('[data-selling-plan-groups]', this) || [];
    this.#currentVariant = this.#readJson('[data-current-variant]', this);
    this.#defaultButtonText = 'Get Started';
    this.#buyOnceBaseText = this.#buyOnceButton?.textContent?.trim() || 'Buy one time';

    this.#prepareInstanceControls();

    this.#form?.addEventListener('submit', this.#handleSubmit, { signal, capture: true });
    this.addEventListener('click', this.#handleProtocolButtonClick, { signal, capture: true });
    this.#buyOnceButton?.addEventListener('click', this.#handleBuyOnce, { signal });

    for (const radio of this.querySelectorAll('input[type="radio"][name^="sunpowder-protocol-offer"]')) {
      radio.addEventListener('change', this.#handleOfferChange, { signal });
    }

    const section = this.closest('.shopify-section') || document.body;
    section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.#syncInterface();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('form[data-type="add-to-cart-form"]'));
  }

  get #selectedOffer() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][name^="sunpowder-protocol-offer"]:checked')
    );

    return /** @type {HTMLElement | null} */ (checkedInput?.closest('[data-protocol-offer]')) || this.#offers[0] || null;
  }

  get #offers() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-protocol-offer]')]);
  }

  get #variantInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-protocol-variant-id]'));
  }

  get #quantityInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-protocol-quantity]'));
  }

  get #sellingPlanInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-protocol-selling-plan]'));
  }

  get #addButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('button[name="add"]'));
  }

  get #buttonTextElement() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.add-to-cart-text__content span span'));
  }

  get #buyOnceButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-protocol-buy-once]'));
  }

  get #errorElement() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-protocol-error]'));
  }

  get #errorTextElement() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-protocol-error-text]'));
  }

  get #liveRegion() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-protocol-live-region]'));
  }

  /**
   * @param {Event} event
   */
  #handleOfferChange = (event) => {
    const target = /** @type {HTMLInputElement | null} */ (event.target);
    if (!target?.checked) return;

    this.#hideError();
    this.#syncInterface();
  };

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();

    await this.#submitSelectedOffer();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleProtocolButtonClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('button[name="add"]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    await this.#submitSelectedOffer();
  };

  #submitSelectedOffer = async () => {
    const offer = this.#selectedOffer;
    if (!offer || this.hasAttribute(SUBMITTING_ATTRIBUTE)) return;

    this.#hideError();
    this.#syncInterface();

    const payload = this.#buildSubscriptionPayload(offer);
    if (!payload) return;

    this.#setSubmitting(true);

    try {
      const response = await this.#addToCart(payload);

      if (response.status || response.errors) {
        this.#handleCartError(response);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(response, this.#sourceId, {
          source: 'sunpowder-protocol-buy-box',
          itemCount: payload.items.reduce((total, item) => total + item.quantity, 0),
          productId: this.dataset.productId,
          variantId: this.#currentVariant?.id?.toString(),
          sections: response.sections,
        })
      );

      this.#announce(Theme.translations?.added || 'Added');
    } catch (error) {
      this.#handleCartError({
        message: 'Unable to add this protocol to cart.',
        description: error instanceof Error ? error.message : '',
      });
    } finally {
      this.#setSubmitting(false);
      this.#syncInterface();
    }
  };

  #handleBuyOnce = async () => {
    if (this.hasAttribute(SUBMITTING_ATTRIBUTE)) return;

    this.#hideError();
    this.#syncInterface();

    const variantId = this.#currentVariant?.id || this.#variantInput?.value;
    if (!variantId || !this.#isVariantAvailable()) {
      this.#handleCartError({ message: 'This variant is currently unavailable.' });
      return;
    }

    this.#setSubmitting(true);

    try {
      const response = await this.#addToCart({
        items: [{ id: Number(variantId), quantity: 1 }],
      });

      if (response.status || response.errors) {
        this.#handleCartError(response);
        return;
      }

      window.location.href = '/checkout';
    } catch (error) {
      this.#handleCartError({
        message: 'Unable to start checkout.',
        description: error instanceof Error ? error.message : '',
      });
    } finally {
      this.#setSubmitting(false);
      this.#syncInterface();
    }
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail?.data?.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const nextBox = this.#findUpdatedBox(event.detail?.data?.html);
    const nextVariant = this.#readJson('[data-current-variant]', nextBox) || event.detail?.resource;
    const nextSellingPlanGroups = this.#readJson('[data-selling-plan-groups]', nextBox);

    if (nextVariant) this.#currentVariant = nextVariant;
    if (nextSellingPlanGroups) this.#sellingPlanGroups = nextSellingPlanGroups;
    this.#syncPowderImage(nextBox);
    this.#prepareInstanceControls();

    this.#syncInterface();
  };

  /**
   * @param {Document | null | undefined} html
   * @returns {HTMLElement | null}
   */
  #findUpdatedBox(html) {
    if (!html) return null;

    const boxes = /** @type {HTMLElement[]} */ ([...html.querySelectorAll('sunpowder-protocol-buy-box')]);
    return boxes.find((box) => box.dataset.sectionId === this.dataset.sectionId) || boxes[0] || null;
  }

  /**
   * @param {HTMLElement | null} nextBox
   */
  #syncPowderImage(nextBox) {
    if (!nextBox) return;

    const currentWrap = this.querySelector('[data-protocol-powder-image-wrap]');
    const nextWrap = nextBox.querySelector('[data-protocol-powder-image-wrap]');
    if (!currentWrap || !nextWrap) return;

    currentWrap.innerHTML = nextWrap.innerHTML;
  }

  #prepareInstanceControls() {
    const radioName = `sunpowder-protocol-offer-${this.#instanceId}`;
    for (const radio of this.querySelectorAll('input[type="radio"][name^="sunpowder-protocol-offer"]')) {
      radio.setAttribute('name', radioName);
    }

    if (this.#form?.id) this.#form.id = `${this.#form.id}-${this.#instanceId}`;
    if (this.#addButton?.id) this.#addButton.id = `${this.#addButton.id}-${this.#instanceId}`;

    if (this.#addButton) {
      this.#addButton.dataset.puppet = 'true';
      this.#addButton.type = 'button';
      this.#addButton.removeAttribute('data-added');
    }
  }

  #syncInterface() {
    const variantId = this.#currentVariant?.id || '';
    const variantPrice = this.#variantPrice;
    const variantAvailable = this.#isVariantAvailable();

    if (this.#variantInput) this.#variantInput.value = variantId.toString();

    const offerPrices = new Map();

    for (const offer of this.#offers) {
      const sellingPlan = this.#findSellingPlan(offer);
      const allocation = this.#findAllocation(sellingPlan);
      const unitPrice = this.#allocationUnitPrice(allocation, sellingPlan);
      const quantity = this.#offerQuantity(offer);
      const total = unitPrice * quantity;

      offerPrices.set(offer, { allocation, quantity, sellingPlan, total, unitPrice });
      this.#updateOfferPrice(offer, unitPrice, total, variantPrice);
    }

    const selectedOffer = this.#selectedOffer;
    const selectedData = selectedOffer ? offerPrices.get(selectedOffer) : null;

    if (selectedOffer && this.#quantityInput) this.#quantityInput.value = selectedData?.quantity?.toString() || '1';
    if (this.#sellingPlanInput) {
      const planId = selectedData?.sellingPlan?.id;
      this.#sellingPlanInput.value = planId ? planId.toString() : '';
      this.#sellingPlanInput.disabled = !planId;
    }

    const oneTimeText = `${this.#buyOnceLabel} for ${this.#formatMoney(variantPrice)} ${this.dataset.buyOnceShippingText || '+ shipping'}`;
    if (this.#buyOnceButton) {
      this.#buyOnceButton.textContent = oneTimeText.trim();
      this.#buyOnceButton.disabled = !variantAvailable || this.hasAttribute(SUBMITTING_ATTRIBUTE);
    }

    const isProtocolSelected = selectedOffer?.dataset.offerKey === 'protocol';
    const missingProtocolSetup = isProtocolSelected && this.#isProtocolMissingBonus(selectedOffer);
    const hasSellingPlan = Boolean(selectedData?.sellingPlan?.id);
    const canSubmit = variantAvailable && hasSellingPlan && !missingProtocolSetup && !this.hasAttribute(SUBMITTING_ATTRIBUTE);

    this.#setAddButtonDisabled(!canSubmit);

    if (!variantAvailable) {
      this.#setButtonText('Sold out');
    } else if (missingProtocolSetup) {
      this.#setButtonText('Configure bonus products');
    } else if (!hasSellingPlan) {
      this.#setButtonText('Selling plan unavailable');
    } else {
      this.#setButtonText(this.#defaultButtonText);
    }
  }

  /**
   * @param {HTMLElement} offer
   * @returns {{ items: Array<{ id: number, quantity: number, selling_plan?: number }>, sections?: string, sections_url?: string } | null}
   */
  #buildSubscriptionPayload(offer) {
    const sellingPlan = this.#findSellingPlan(offer);
    const variantId = Number(this.#currentVariant?.id || this.#variantInput?.value);
    const quantity = this.#offerQuantity(offer);

    if (!variantId || !this.#isVariantAvailable()) {
      this.#handleCartError({ message: 'This variant is currently unavailable.' });
      return null;
    }

    if (!sellingPlan?.id) {
      this.#handleCartError({ message: 'The matching subscription plan is unavailable for this variant.' });
      return null;
    }

    const items = [{ id: variantId, quantity, selling_plan: Number(sellingPlan.id) }];

    if (offer.dataset.offerKey === 'protocol') {
      if (this.#isProtocolMissingBonus(offer)) {
        this.#handleCartError({ message: this.dataset.missingBonusError || 'Set up all three bonus products before selling the 90-day protocol.' });
        return null;
      }

      if (this.#isProtocolUsingMainProduct(offer, variantId)) {
        this.#handleCartError({ message: 'Choose dedicated $0 bonus products. A bonus product is currently set to Sun Powder.' });
        return null;
      }

      items.push(
        { id: Number(offer.dataset.bonusBookVariantId), quantity: 1 },
        { id: Number(offer.dataset.bonusGroupVariantId), quantity: 1 },
        { id: Number(offer.dataset.bonusMixerVariantId), quantity: 1 }
      );
    }

    const sections = this.#cartSectionIds.join(',');

    return {
      items,
      ...(sections ? { sections, sections_url: window.location.pathname } : {}),
    };
  }

  /**
   * @param {{ items: Array<{ id: number, quantity: number, selling_plan?: number }>, sections?: string, sections_url?: string }} payload
   */
  async #addToCart(payload) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), CART_REQUEST_TIMEOUT);

    try {
      const response = await fetch(globalThis.Theme?.routes?.cart_add_url || '/cart/add.js', {
        ...fetchConfig('json', { body: JSON.stringify(payload) }),
        signal: controller.signal,
      });
      const text = await response.text();
      let data = {};

      if (text) {
        try {
          data = JSON.parse(text);
        } catch (_error) {
          data = { description: text };
        }
      }

      if (!response.ok) {
        data.status = data.status || response.status;
        data.message = data.message || response.statusText || 'Unable to add this item to cart.';
      }

      return data;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return {
          status: 408,
          message: 'The cart took too long to respond. Please try again.',
        };
      }

      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  /**
   * @param {HTMLElement} offer
   * @returns {any}
   */
  #findSellingPlan(offer) {
    const targetName = (offer.dataset.sellingPlanName || '').trim().toLowerCase();
    const targetDays = parseInt(offer.dataset.deliveryDays || '', 10);

    for (const group of this.#sellingPlanGroups) {
      for (const plan of group.selling_plans || []) {
        if (targetName && plan.name?.trim().toLowerCase() === targetName) return plan;
      }
    }

    if (!targetDays) return null;

    for (const group of this.#sellingPlanGroups) {
      for (const plan of group.selling_plans || []) {
        const planDays = this.#daysFromPlanName(plan.name || '');
        if (planDays === targetDays) return plan;
      }
    }

    return null;
  }

  /**
   * @param {any} sellingPlan
   * @returns {any}
   */
  #findAllocation(sellingPlan) {
    if (!sellingPlan || !this.#currentVariant?.selling_plan_allocations) return null;

    const planId = sellingPlan.id?.toString();
    const allocations = this.#currentVariant.selling_plan_allocations;

    return (
      allocations.find(
        (allocation) =>
          allocation.selling_plan_id?.toString() === planId || allocation.selling_plan?.id?.toString() === planId
      ) ||
      allocations.find((allocation) => allocation.selling_plan?.name === sellingPlan.name) ||
      null
    );
  }

  /**
   * @param {any} allocation
   * @param {any} sellingPlan
   * @returns {number}
   */
  #allocationUnitPrice(allocation, sellingPlan) {
    const allocationPrice = Number(allocation?.price ?? allocation?.per_delivery_price);
    if (Number.isFinite(allocationPrice) && allocationPrice > 0) return allocationPrice;

    const adjustment = sellingPlan?.price_adjustments?.[0];
    const basePrice = this.#variantPrice;

    if (!adjustment) return basePrice;

    if (adjustment.value_type === 'percentage') {
      return Math.round(basePrice * (1 - Number(adjustment.value || 0) / 100));
    }

    if (adjustment.value_type === 'fixed_amount') {
      return Math.max(0, basePrice - Number(adjustment.value || 0));
    }

    if (adjustment.value_type === 'price') {
      return Number(adjustment.value || basePrice);
    }

    return basePrice;
  }

  /**
   * @param {HTMLElement} offer
   * @param {number} unitPrice
   * @param {number} total
   * @param {number} variantPrice
   */
  #updateOfferPrice(offer, unitPrice, total, variantPrice) {
    const priceElement = /** @type {HTMLElement | null} */ (offer.querySelector('[data-protocol-price]'));
    const billingElement = /** @type {HTMLElement | null} */ (offer.querySelector('[data-protocol-billing]'));
    const quantity = this.#offerQuantity(offer);
    const months = Math.max(1, Math.round((parseInt(offer.dataset.deliveryDays || '30', 10) || 30) / 30));

    if (priceElement) priceElement.textContent = `${this.#formatMoney(unitPrice)}/mo`;

    if (billingElement) {
      billingElement.textContent =
        months > 1 ? `${this.#formatMoney(total)} billed every ${months} mo.` : `${this.#formatMoney(total)} billed monthly`;
    }

    if (offer.dataset.offerKey === 'protocol') {
      const savingsElement = /** @type {HTMLElement | null} */ (offer.querySelector('[data-protocol-savings]'));
      const baseTotal = variantPrice * quantity;
      const savings = baseTotal > total ? Math.round(((baseTotal - total) / baseTotal) * 100) : 0;
      if (savingsElement) savingsElement.textContent = savings > 0 ? `Save ${savings}%` : 'Best Value';
    }
  }

  /**
   * @param {HTMLElement | null} offer
   * @returns {boolean}
   */
  #isProtocolMissingBonus(offer) {
    if (!offer || offer.dataset.offerKey !== 'protocol') return false;
    if (this.dataset.protocolMissingBonus === 'true') return true;

    return !offer.dataset.bonusBookVariantId || !offer.dataset.bonusGroupVariantId || !offer.dataset.bonusMixerVariantId;
  }

  /**
   * @param {HTMLElement | null} offer
   * @param {number} variantId
   * @returns {boolean}
   */
  #isProtocolUsingMainProduct(offer, variantId) {
    if (!offer || offer.dataset.offerKey !== 'protocol') return false;

    const productId = Number(this.dataset.productId);
    const bonusProductIds = [
      Number(offer.dataset.bonusBookProductId),
      Number(offer.dataset.bonusGroupProductId),
      Number(offer.dataset.bonusMixerProductId),
    ];
    const bonusVariantIds = [
      Number(offer.dataset.bonusBookVariantId),
      Number(offer.dataset.bonusGroupVariantId),
      Number(offer.dataset.bonusMixerVariantId),
    ];

    return bonusProductIds.some((id) => id && id === productId) || bonusVariantIds.some((id) => id && id === variantId);
  }

  #isVariantAvailable() {
    return Boolean(this.#currentVariant?.available);
  }

  get #variantPrice() {
    return Number(this.#currentVariant?.price || this.dataset.variantPrice || 0);
  }

  get #sourceId() {
    return this.#form?.id || this.id || 'sunpowder-protocol-buy-box';
  }

  get #buyOnceLabel() {
    return this.#buyOnceBaseText || 'Buy one time';
  }

  get #cartSectionIds() {
    const sectionIds = new Set();
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.add(item.dataset.sectionId);
    }
    return [...sectionIds];
  }

  /**
   * @param {HTMLElement} offer
   * @returns {number}
   */
  #offerQuantity(offer) {
    return parseInt(offer.dataset.quantity || '1', 10) || 1;
  }

  /**
   * @param {string} name
   * @returns {number | null}
   */
  #daysFromPlanName(name) {
    const daysMatch = name.match(/(\d+)\s*days?/i);
    if (daysMatch?.[1]) return parseInt(daysMatch[1], 10);

    const monthsMatch = name.match(/(\d+)\s*months?/i);
    if (monthsMatch?.[1]) return parseInt(monthsMatch[1], 10) * 30;

    return null;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    const options = {
      style: 'currency',
      currency,
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    };

    return new Intl.NumberFormat('en-US', options).format(amount);
  }

  /**
   * @param {string} selector
   * @param {ParentNode | null | undefined} root
   * @returns {any}
   */
  #readJson(selector, root) {
    const script = root?.querySelector(selector);
    const text = script?.textContent?.trim();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch (error) {
      console.warn('Unable to parse protocol buy box JSON:', error);
      return null;
    }
  }

  /**
   * @param {boolean} isSubmitting
   */
  #setSubmitting(isSubmitting) {
    this.toggleAttribute(SUBMITTING_ATTRIBUTE, isSubmitting);
    this.#setAddButtonDisabled(isSubmitting);
    if (this.#buyOnceButton) this.#buyOnceButton.disabled = isSubmitting;
    if (!isSubmitting) this.#addButton?.removeAttribute('data-added');
  }

  /**
   * @param {boolean} disabled
   */
  #setAddButtonDisabled(disabled) {
    const button = this.#addButton;
    if (!button) return;

    button.disabled = disabled;
    const container = /** @type {any} */ (button.closest('add-to-cart-component'));
    if (disabled) {
      container?.disable?.();
    } else {
      container?.enable?.();
    }
  }

  /**
   * @param {string} text
   */
  #setButtonText(text) {
    const textElement = this.#buttonTextElement;
    if (textElement) textElement.textContent = text || this.#defaultButtonText;
  }

  /**
   * @param {any} response
   */
  #handleCartError(response) {
    const message = response.message || response.description || response.errors || 'Something went wrong. Please try again.';

    this.dispatchEvent(new CartErrorEvent(this.#sourceId, message, response.description, response.errors));
    this.#showError(message);
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    if (this.#errorTextElement) this.#errorTextElement.textContent = message;
    this.#errorElement?.classList.remove('hidden');
    this.#announce(message);
  }

  #hideError() {
    if (this.#errorTextElement) this.#errorTextElement.textContent = '';
    this.#errorElement?.classList.add('hidden');
    this.#announce('');
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    if (this.#liveRegion) this.#liveRegion.textContent = message;
  }
}

if (!customElements.get('sunpowder-protocol-buy-box')) {
  customElements.define('sunpowder-protocol-buy-box', SunpowderProtocolBuyBox);
}
